import React from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { formatTime } from './routePlannerUtils';

function RoutePlanBoard({ plan, loading, publishPlan, moveStop, moveStopToRoute, moveDrafts, setMoveDrafts }) {
  if (!plan) return null;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">
            Plan #{plan.id} - {plan.status}
          </h2>
          <p className="text-sm text-slate-500">
            {plan.optimization_provider} | {plan.summary?.assigned_stops || 0} asignadas | {plan.unassigned?.length || 0} sin asignar
          </p>
          {plan.summary?.google_fallback_reason && (
            <p className="mt-1 text-xs font-semibold text-amber-700">
              Google fallback: {plan.summary.google_fallback_reason}
            </p>
          )}
        </div>
        <button
          onClick={publishPlan}
          disabled={loading || plan.status === 'published'}
          className="rounded-lg bg-brand-green px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
        >
          Publicar rutas
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {(plan.routes || []).map((route) => (
          <RouteColumn
            key={route.id}
            route={route}
            routes={plan.routes || []}
            moveStop={moveStop}
            moveStopToRoute={moveStopToRoute}
            moveDrafts={moveDrafts}
            setMoveDrafts={setMoveDrafts}
          />
        ))}
      </div>

      {!!plan.unassigned?.length && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 flex items-center gap-2 font-bold text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Sin asignar
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {plan.unassigned.map((item) => (
              <div key={`${item.delivery_point_id}-${item.reason}`} className="rounded-lg bg-white p-2 text-sm">
                <span className="font-bold">#{item.delivery_point_id}</span>{' '}
                {item.delivery?.district || 'Sin distrito'} - {item.reason}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function RouteColumn({ route, routes, moveStop, moveStopToRoute, moveDrafts, setMoveDrafts }) {
  return (
    <div className="rounded-lg border border-slate-200">
      <div className="border-b border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-bold text-slate-900">{route.driver_name || `Driver ${route.driver_id}`}</h3>
            <p className="text-xs text-slate-500">
              {route.total_stops} paradas | fin {formatTime(route.estimated_end_time)}
            </p>
          </div>
          <Play className="h-4 w-4 text-brand-green" />
        </div>
      </div>
      <div className="max-h-[520px] space-y-2 overflow-y-auto p-3">
        {(route.stops || []).map((stop) => (
          <RouteStopCard
            key={stop.id}
            stop={stop}
            route={route}
            routes={routes}
            moveStop={moveStop}
            moveStopToRoute={moveStopToRoute}
            moveDrafts={moveDrafts}
            setMoveDrafts={setMoveDrafts}
          />
        ))}
        {!route.stops?.length && (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
            Sin paradas
          </div>
        )}
      </div>
    </div>
  );
}

function RouteStopCard({ stop, route, routes, moveStop, moveStopToRoute, moveDrafts, setMoveDrafts }) {
  const draft = moveDrafts[stop.id] || {};
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-slate-900">
            #{stop.sequence} - {stop.delivery?.recipient_name}
          </div>
          <div className="text-xs text-slate-500">
            {stop.delivery?.district || 'Sin distrito'} | slot {stop.delivery?.slot || 0} | ETA {formatTime(stop.planned_arrival_time)}
          </div>
        </div>
        {stop.is_priority && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-700">Prioridad</span>}
      </div>
      {stop.warning && <div className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">{stop.warning}</div>}
      <div className="mb-2 text-xs text-slate-600">{stop.delivery?.address}</div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => moveStop(stop, route.id, -1)} className="rounded-lg border border-slate-200 p-1 text-slate-600" title="Subir">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button onClick={() => moveStop(stop, route.id, 1)} className="rounded-lg border border-slate-200 p-1 text-slate-600" title="Bajar">
          <ChevronDown className="h-4 w-4" />
        </button>
        <select
          value={draft.target_route_id || route.id}
          onChange={(event) => setMoveDrafts((current) => ({ ...current, [stop.id]: { ...current[stop.id], target_route_id: event.target.value } }))}
          className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs"
        >
          {routes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.driver_name || `Driver ${item.driver_id}`}
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          value={draft.target_sequence || ''}
          onChange={(event) => setMoveDrafts((current) => ({ ...current, [stop.id]: { ...current[stop.id], target_sequence: event.target.value } }))}
          className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs"
          placeholder="#"
        />
        <button onClick={() => moveStopToRoute(stop)} className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-bold text-white">
          Mover
        </button>
      </div>
    </div>
  );
}

export default RoutePlanBoard;
